import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess, PaginationMeta } from '../types';

export type IntegrationCategory = 'crm' | 'oms' | 'erp' | 'logistics' | 'sap' | 'tally' | 'oem' | 'cpo' | 'custom';
export type IntegrationDirection = 'inbound' | 'outbound' | 'bidirectional';
export type IntegrationTransport = 'rest' | 'webhook' | 'sftp' | 'csv';
export type IntegrationAuthType = 'none' | 'api_key' | 'oauth2' | 'hmac' | 'basic';
export type IntegrationStatus = 'draft' | 'active' | 'paused' | 'error' | 'disabled';
export type SecretKind = 'api_key' | 'oauth2' | 'basic' | 'hmac' | 'sftp';

export interface Integration {
  id: number;
  code: string;
  name: string;
  category: IntegrationCategory;
  connector_key: string;
  direction: IntegrationDirection;
  transport: IntegrationTransport;
  auth_type: IntegrationAuthType;
  status: IntegrationStatus;
  base_url: string | null;
  config: Record<string, unknown> | null;
  health_status: 'unknown' | 'healthy' | 'degraded' | 'down';
  last_sync_at: string | null;
  last_error: string | null;
  created_at: string;
}

export interface CreateIntegrationInput {
  name: string;
  category: IntegrationCategory;
  connectorKey: string;
  direction?: IntegrationDirection;
  transport?: IntegrationTransport;
  authType?: IntegrationAuthType;
  baseUrl?: string | null;
  config?: Record<string, unknown> | null;
}

export interface ConnectorMeta {
  key: string;
  label: string;
  category: IntegrationCategory;
  description: string;
  transports: IntegrationTransport[];
  authTypes: IntegrationAuthType[];
  actions: string[];
  configFields: Array<{ key: string; label: string; required: boolean; secret?: boolean }>;
}

export interface IntegrationDashboard {
  kpis: {
    totalIntegrations: number; activeIntegrations: number; erroredIntegrations: number;
    activeApiKeys: number; totalCalls: number; errorRate: number; avgDurationMs: number;
  };
  charts: {
    byCategory: Array<{ label: string; value: number }>;
    byDirection: Array<{ label: string; value: number }>;
    byStatus: Array<{ label: string; value: number }>;
  };
}

export interface ApiKey {
  id: number;
  name: string;
  key_prefix: string;
  scopes: string[] | null;
  rate_limit_per_min: number;
  ip_whitelist: string[] | null;
  status: 'active' | 'revoked';
  integration_id: number | null;
  expires_at: string | null;
  last_used_at: string | null;
  usage_count: number;
  created_at: string;
}

export interface IntegrationLog {
  id: number;
  integration_id: number | null;
  api_key_id: number | null;
  direction: 'inbound' | 'outbound';
  method: string | null;
  url: string | null;
  event: string | null;
  response_status: number | null;
  duration_ms: number | null;
  status: 'success' | 'error';
  error_message: string | null;
  created_at: string;
}

export interface IntegrationAnalytics {
  totalCalls: number; errors: number; successRate: number; avgDurationMs: number;
  byDirection: Array<{ label: string; value: number }>;
  byStatus: Array<{ label: string; value: number }>;
  topIntegrations: Array<{ integrationId: number; value: number }>;
}

const base = createCrudApi<Integration, CreateIntegrationInput>('integrations');

export const integrationApi = {
  ...base,
  async marketplace(): Promise<ConnectorMeta[]> {
    return (await apiClient.get<ApiSuccess<ConnectorMeta[]>>('/integrations/marketplace')).data.data;
  },
  async dashboard(): Promise<IntegrationDashboard> {
    return (await apiClient.get<ApiSuccess<IntegrationDashboard>>('/integrations/dashboard')).data.data;
  },
  async activate(id: number): Promise<Integration> {
    return (await apiClient.post<ApiSuccess<Integration>>(`/integrations/${id}/activate`, {})).data.data;
  },
  async pause(id: number): Promise<Integration> {
    return (await apiClient.post<ApiSuccess<Integration>>(`/integrations/${id}/pause`, {})).data.data;
  },
  async test(id: number): Promise<{ ok: boolean; status: number; message: string; simulated: boolean; durationMs: number }> {
    return (await apiClient.post<ApiSuccess<{ ok: boolean; status: number; message: string; simulated: boolean; durationMs: number }>>(`/integrations/${id}/test`, {})).data.data;
  },
  async setCredential(id: number, kind: SecretKind, secret: string, meta?: Record<string, unknown> | null): Promise<{ kind: SecretKind; stored: true }> {
    return (await apiClient.post<ApiSuccess<{ kind: SecretKind; stored: true }>>(`/integrations/${id}/credentials`, { kind, secret, meta })).data.data;
  },
  async docs(): Promise<IntegrationDocs> {
    return (await apiClient.get<ApiSuccess<IntegrationDocs>>('/integrations/docs')).data.data;
  },
  async invoke(id: number, action: string, payload: Record<string, unknown> = {}): Promise<{ action: string; ok: boolean; status: number; data: unknown; simulated: boolean; durationMs: number }> {
    return (await apiClient.post<ApiSuccess<{ action: string; ok: boolean; status: number; data: unknown; simulated: boolean; durationMs: number }>>(`/integrations/${id}/invoke`, { action, payload })).data.data;
  },
  async importCsv(id: number, fileName: string, content: string): Promise<{ fileId: number; rowCount: number; preview: Array<Record<string, string>> }> {
    return (await apiClient.post<ApiSuccess<{ fileId: number; rowCount: number; preview: Array<Record<string, string>> }>>(`/integrations/${id}/import`, { fileName, content })).data.data;
  },
  async sftpPull(id: number): Promise<{ fileId: number; rowCount: number; preview: Array<Record<string, string>> }> {
    return (await apiClient.post<ApiSuccess<{ fileId: number; rowCount: number; preview: Array<Record<string, string>> }>>(`/integrations/${id}/sftp-pull`, {})).data.data;
  },
  async files(id: number): Promise<IntegrationFile[]> {
    return (await apiClient.get<ApiSuccess<IntegrationFile[]>>(`/integrations/${id}/files`)).data.data;
  },
};

export interface IntegrationFile {
  id: number; integration_id: number; direction: 'import' | 'export'; transport: string; format: string;
  file_name: string; row_count: number; status: string; error: string | null; created_at: string;
}
export interface IntegrationDocs {
  overview: string;
  authentication: Array<{ method: string; how: string }>;
  gateway: Array<{ method: string; path: string; auth: string; description: string }>;
  webhooks: { outgoing: { description: string; headers: string[] }; retry: { strategy: string; formula: string; deadLetter: string } };
  transports: string[];
  connectors: ConnectorMeta[];
}
export interface IntegrationMonitoring {
  kpis: { totalCalls: number; errors: number; successRate: number };
  charts: {
    volumeTrend: Array<{ label: string; value: number }>;
    errorTrend: Array<{ label: string; value: number }>;
    statusCodes: Array<{ label: string; value: number }>;
    latency: Array<{ label: string; value: number }>;
    topIntegrations: Array<{ label: string; value: number }>;
  };
  recentErrors: Array<{ id: number; integration: string | null; direction: string; event: string | null; responseStatus: number | null; error: string | null }>;
}

export interface IssueApiKeyInput {
  name: string;
  scopes?: string[];
  rateLimitPerMin?: number;
  ipWhitelist?: string[];
  integrationId?: number | null;
  expiresAt?: string | null;
}

export const apiKeyApi = {
  async list(params: Record<string, unknown> = {}): Promise<{ data: ApiKey[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<ApiKey[]>>('/integration-keys', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  async issue(input: IssueApiKeyInput): Promise<{ apiKey: ApiKey; plaintextKey: string }> {
    return (await apiClient.post<ApiSuccess<{ apiKey: ApiKey; plaintextKey: string }>>('/integration-keys', input)).data.data;
  },
  async revoke(id: number): Promise<ApiKey> {
    return (await apiClient.post<ApiSuccess<ApiKey>>(`/integration-keys/${id}/revoke`, {})).data.data;
  },
};

export const integrationLogApi = {
  async list(params: Record<string, unknown> = {}): Promise<{ data: IntegrationLog[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<IntegrationLog[]>>('/integration-logs', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  async analytics(): Promise<IntegrationAnalytics> {
    return (await apiClient.get<ApiSuccess<IntegrationAnalytics>>('/integration-logs/analytics')).data.data;
  },
  async monitoring(): Promise<IntegrationMonitoring> {
    return (await apiClient.get<ApiSuccess<IntegrationMonitoring>>('/integration-logs/monitoring')).data.data;
  },
};

// ---- Increment 2: webhooks, deliveries, incoming, jobs, OAuth2 ----
export interface WebhookEndpoint {
  id: number; name: string; target_url: string; event_types: string[]; headers: Record<string, string> | null;
  status: 'active' | 'paused' | 'disabled'; max_retries: number; failure_count: number; last_delivery_at: number | null; integration_id: number | null; created_at: string;
}
export interface WebhookDelivery {
  id: number; webhook_endpoint_id: number; event_type: string; status: 'pending' | 'success' | 'failed' | 'retrying' | 'dead';
  attempt_count: number; max_attempts: number; next_attempt_at: number | null; response_status: number | null; duration_ms: number | null; last_error: string | null; created_at: string;
}
export interface IncomingWebhook {
  id: number; slug: string; name: string; source: string | null; signature_header: string; ip_whitelist: string[]; handler_key: string; status: string; received_count: number; created_at: string;
}
export interface IntegrationJob {
  id: number; integration_id: number | null; job_type: string; reference_type: string | null; reference_id: number | null;
  status: 'queued' | 'running' | 'success' | 'failed' | 'dead'; attempts: number; max_attempts: number; scheduled_at: number; last_error: string | null; created_at: string;
}

export const webhookApi = {
  async listEndpoints(params: Record<string, unknown> = {}): Promise<{ data: WebhookEndpoint[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<WebhookEndpoint[]>>('/webhook-endpoints', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  async createEndpoint(input: { name: string; targetUrl: string; eventTypes?: string[]; maxRetries?: number }): Promise<{ endpoint: WebhookEndpoint; signingSecret: string }> {
    return (await apiClient.post<ApiSuccess<{ endpoint: WebhookEndpoint; signingSecret: string }>>('/webhook-endpoints', input)).data.data;
  },
  async setStatus(id: number, action: 'activate' | 'pause'): Promise<WebhookEndpoint> {
    return (await apiClient.post<ApiSuccess<WebhookEndpoint>>(`/webhook-endpoints/${id}/${action}`, {})).data.data;
  },
  async remove(id: number): Promise<void> { await apiClient.delete(`/webhook-endpoints/${id}`); },
  async test(id: number): Promise<WebhookDelivery> {
    return (await apiClient.post<ApiSuccess<WebhookDelivery>>(`/webhook-endpoints/${id}/test`, {})).data.data;
  },
  async dispatch(eventType: string, payload: Record<string, unknown> = {}): Promise<{ dispatched: number }> {
    return (await apiClient.post<ApiSuccess<{ dispatched: number }>>('/webhook-endpoints/dispatch', { eventType, payload })).data.data;
  },
  async deliveries(endpointId: number): Promise<WebhookDelivery[]> {
    return (await apiClient.get<ApiSuccess<WebhookDelivery[]>>(`/webhook-endpoints/${endpointId}/deliveries`)).data.data;
  },
  async replay(deliveryId: number): Promise<WebhookDelivery> {
    return (await apiClient.post<ApiSuccess<WebhookDelivery>>(`/webhook-deliveries/${deliveryId}/replay`, {})).data.data;
  },
  async listIncoming(params: Record<string, unknown> = {}): Promise<{ data: IncomingWebhook[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<IncomingWebhook[]>>('/incoming-webhooks', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  async createIncoming(input: { name: string; slug: string; source?: string; handlerKey?: string }): Promise<{ incoming: IncomingWebhook; hmacSecret: string }> {
    return (await apiClient.post<ApiSuccess<{ incoming: IncomingWebhook; hmacSecret: string }>>('/incoming-webhooks', input)).data.data;
  },
  async removeIncoming(id: number): Promise<void> { await apiClient.delete(`/incoming-webhooks/${id}`); },
};

export const jobApi = {
  async list(params: Record<string, unknown> = {}): Promise<{ data: IntegrationJob[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<IntegrationJob[]>>('/integration-jobs', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  async runDue(): Promise<{ processed: number; success: number; dead: number; requeued: number }> {
    return (await apiClient.post<ApiSuccess<{ processed: number; success: number; dead: number; requeued: number }>>('/integration-jobs/run', {})).data.data;
  },
  async retry(id: number): Promise<{ status: string }> {
    return (await apiClient.post<ApiSuccess<{ status: string }>>(`/integration-jobs/${id}/retry`, {})).data.data;
  },
};

export interface OAuthStatus { id: number; token_type: string; scope: string | null; expires_at: number | null; obtained_at: number; has_refresh_token: boolean }
export const oauthApi = {
  async config(integrationId: number, input: { provider?: string; clientId: string; clientSecret: string; authUrl?: string; tokenUrl: string; grantType?: string; scopes?: string[]; redirectUri?: string }): Promise<unknown> {
    return (await apiClient.put<ApiSuccess<unknown>>(`/integrations/${integrationId}/oauth/config`, input)).data.data;
  },
  async status(integrationId: number): Promise<OAuthStatus | null> {
    return (await apiClient.get<ApiSuccess<OAuthStatus | null>>(`/integrations/${integrationId}/oauth`)).data.data;
  },
  async token(integrationId: number, code?: string): Promise<OAuthStatus> {
    return (await apiClient.post<ApiSuccess<OAuthStatus>>(`/integrations/${integrationId}/oauth/token`, { code })).data.data;
  },
  async refresh(integrationId: number): Promise<OAuthStatus> {
    return (await apiClient.post<ApiSuccess<OAuthStatus>>(`/integrations/${integrationId}/oauth/refresh`, {})).data.data;
  },
};
